import {
    CompanyContracts,
    CompanyContractsViewData,
    Contract,
    ContractCoverage,
    ContractState,
    FrameworkContract,
} from "../../redux/contracts/contracts-types";

/**
 * Get the latest active (or pending if none is active) contract from a list of contracts.
 *
 * @param contracts List of contracts to search in.
 * @returns Latest active contract, or the latest pending contract if no active contract is found, or null if no active
 * or pending contract is found.
 */
export function getCurrentContract(contracts: Contract[]): Contract | null {
    const mostRecentFirst = [...contracts].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
    const active = mostRecentFirst.find((contract) => contract.state === ContractState.ACTIVE);
    if (active !== undefined) {
        return active;
    }
    const pending = mostRecentFirst.find((contract) => contract.state === ContractState.PENDING);
    if (pending !== undefined) {
        return pending;
    }
    return null;
}

export function companyContractsViewData(companyContracts: CompanyContracts | null): CompanyContractsViewData {
    const contracts = companyContracts ?? {
        userDpaContracts: [],
        companyDpaContracts: [],
        companySubscriptionContracts: [],
        frameworkContract: null,
    };

    const frameworkContract = contracts.frameworkContract;

    const personalDpa = getCurrentContract(companyContracts?.userDpaContracts ?? []);

    const personalDpaCoverage = companyContractCoverage(
        personalDpa,
        (frameworkContract?.coversManagementCompanyDpa && frameworkContract) || null,
        true,
    );

    const companyDpa = getCurrentContract(companyContracts?.companyDpaContracts ?? []);

    const companyDpaCoverage = companyContractCoverage(
        companyDpa,
        (frameworkContract?.coversHousingCompanyDpa && frameworkContract) || null,
        false,
    );

    const companySubscription = getCurrentContract(companyContracts?.companySubscriptionContracts ?? []);

    const subscriptionDpaCoverage = companyContractCoverage(
        companySubscription,
        (frameworkContract?.coversSubscriptionContract && frameworkContract) || null,
        false,
    );

    return {
        subscriptionCoverage: subscriptionDpaCoverage,
        dpaCoverage: companyDpaCoverage,
        managementCompanyDpaCoverage: personalDpaCoverage,
    };
}

/**
 * Get the contract coverage rules based on applicable contract / applicable framework contract.
 * @param contract applicable contract
 * @param applicableFrameworkContract applicable framework contract
 * @param isUserContract whether the contract is a contract signed elsewhere on behalf of the user
 */
function companyContractCoverage(
    contract: Contract | null,
    applicableFrameworkContract: FrameworkContract | null,
    isUserContract: boolean,
): ContractCoverage {
    if (applicableFrameworkContract !== null) {
        return {
            contract: null,
            coveredByFramework: applicableFrameworkContract,
            coveredByUserContract: false,
            isCovered: true,
        };
    }

    if (contract !== null) {
        return {
            contract: contract,
            coveredByFramework: null,
            coveredByUserContract: isUserContract,
            isCovered: contract.state === ContractState.ACTIVE,
        };
    }

    return {
        contract: null,
        coveredByFramework: null,
        coveredByUserContract: isUserContract,
        isCovered: false,
    };
}
